import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  DialogActions,
  Grid,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import QueryBuilder from "../../components/QueryBuilder/Index";
import * as Yup from "yup";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useToast } from "../../services/utils";
import {
  externalGenericQueryFetchColumnsSchema,
  externalQueryFetchColumnsSchema,
} from "../../schemas";
import { getTableColumns } from "../../services/rulesService";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Loader from "../Molecules/Loader/Loader";
import {
  handleAlphanumericChange,
  handleAlphanumericKeyDown,
} from "../../utils/inputValidation";
import EnhancedQueryDialog from "../Dialogs/EnhancedQueryDialog";
import {
  RULE_EXTERNAL_QUERY_TEMPLATES,
  RULE_MERGED_QUERY_TEMPLATES,
  RULE_MISSING_DATA_QUERY_TEMPLATES,
  RULE_MISMATCHED_DATA_QUERY_TEMPLATES,
  RULE_RESOURCE_QUERY_TEMPLATES,
  RULE_RESEARCH_QUERY_SYNTAX_HELP_TEXT,
} from "../../constants/queryTemplates";
import QueryTemplatesCard from "../common/QueryTemplatesCard";

interface QueryBuilderDialogProps {
  columns?: any;
  setColumns?: any;
  title?: any;
  mergeQueryType?: any;
  setMixedFormData?: any;
  index?: any;
  isTextBoxRequied?: any;
  openQueryDialogIndex: number | null;
  setOpenQueryDialogIndex: (index: number | null) => void;
  isFinalQuery?: boolean;
  fetchExternalColumnsData?: any;
  queryType?: string | null;
  queryBuilderType?: string;
  externalCommonValues?: any;
}

const QueryBuilderDialog = ({
  columns,
  setColumns,
  title,
  mergeQueryType,
  setMixedFormData,
  index,
  isTextBoxRequied,
  openQueryDialogIndex,
  setOpenQueryDialogIndex,
  isFinalQuery = true,
  queryBuilderType,
  fetchExternalColumnsData,
  queryType,
  externalCommonValues,
}: QueryBuilderDialogProps) => {
  //context
  const {
    queryBuilderTempValue,
    setQueryBuilderTempValue,
    availColumns,
    tempGlobalVariables,
    setTempGlobalVariables,
    temporaryQueryValue,
    setTemporaryQueryValue,
    setViewInlineVariables,
    setGlobalVariables,
  } = useRuleResourceContext();
  //states
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const { showToast } = useToast();
  const [query, setQuery] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<any>({
    external_source_name: "",
    table_name: "",
    create_temp_table_index: false,
  });

  const [fetchColumnsLoading, setFetchColumnsLoading] = useState(false);

  useEffect(() => {
    if (
      openQueryDialogIndex === index &&
      fetchExternalColumnsData &&
      fetchExternalColumnsData.mixedFormData
    ) {
      if (mergeQueryType === "Query") {
        setQueryBuilderTempValue(
          fetchExternalColumnsData?.formData?.query ?? "",
        );
        setTemporaryQueryValue(fetchExternalColumnsData?.formData?.query ?? "");
      } else if (!isFinalQuery && index !== undefined) {
        const query =
          fetchExternalColumnsData?.mixedFormData?.[index]?.data_pull_query ??
          "";
        setQueryBuilderTempValue(query);
        setTemporaryQueryValue(query);
      }
    }
  }, [
    openQueryDialogIndex, // this is key!
    index,
    isFinalQuery,
  ]);

  // Note: temporaryQueryValue initialization is now handled in the onClick handler
  // to ensure we capture the exact original value from the text field

  useEffect(() => {
    if (
      openQueryDialogIndex !== null &&
      isOpen &&
      fetchExternalColumnsData?.mixedFormData[index]
    ) {
      setQuery(
        fetchExternalColumnsData?.mixedFormData[index]?.data_pull_query || "",
      );
      const resourceId =
        fetchExternalColumnsData?.mixedFormData?.[index]?.resource_id;
      setColumns((prev: any) => {
        return {
          ...prev,
          resourceColumns: [
            {
              name: fetchExternalColumnsData?.mixedFormData[index]
                ?.resource_code,
              columns: prev?.mixedQueryData?.[resourceId]?.columns,
            },
          ],
        };
      });
    }
  }, [openQueryDialogIndex, fetchExternalColumnsData?.mixedFormData[index]]);
  // Auto-open dialog when openQueryDialogIndex matches current index
  useEffect(() => {
    if (openQueryDialogIndex === index) {
      setIsOpen(true);
    }
  }, [openQueryDialogIndex, index]);

  //handle functions
  const handleCloseQueryModal = () => {
    setIsOpen(false);
    setOpenQueryDialogIndex(null);
    setErrors({});
    setTempGlobalVariables({});
    setQuery("");
    setFormData(() => ({
      external_source_name: "",
      table_name: "",
      create_temp_table_index: false,
    }));
    // Restore the original query value (discard unsaved changes)
    setQueryBuilderTempValue(temporaryQueryValue);
    setColumns((prev: any) => ({
      ...prev,
      fetchedColumns: [],
    }));
  };
  const handleChangeQuery = (qry: any, columns: any) => {
    setQuery(qry?.trim());
  };
  const handleSaveQuery = async () => {
    if (!query) {
      setErrors((prevError: any) => ({
        ...prevError,
        query: "Please enter query",
      }));
      return;
    }
    if (
      mergeQueryType === "External" &&
      externalCommonValues?.fetchFrom === "MixedExternalBuilder"
    ) {
      try {
        let validateData = {};

        validateData = {
          table_name: formData?.table_name,
          external_source_name: formData?.external_source_name,
          should_require_table_name: false,
          should_require_external_source_name: true,
        };
        await externalGenericQueryFetchColumnsSchema.validate(validateData, {
          abortEarly: false,
        });
      } catch (err: any) {
        if (err?.inner) {
          err.inner.forEach((error: any) => {
            setErrors((prevError: any) => ({
              ...prevError,
              [error.path]: error.message,
            }));
          });
        }
        return;
      }
    }

    const filterRuleSchema: any = {};

    Object.keys(tempGlobalVariables).forEach((key: any) => {
      if (tempGlobalVariables[key] === "") {
        filterRuleSchema[key] = Yup.string().required(`Please enter ${key}`);
      }
    });

    setGlobalVariables((prev: any) => ({
      ...Object.keys(tempGlobalVariables).reduce(
        (acc, key) => ({ ...acc, [key]: tempGlobalVariables[key] }),
        prev,
      ),
    }));

    setViewInlineVariables((prev: any) => {
      if (!prev) return prev;

      return {
        ...prev,
        comparison_research_query_variables: {
          ...prev.comparison_research_query_variables, // keep existing
          ...tempGlobalVariables, // add/overwrite with temp values
        },
      };
    });

    // try {
    //   await Yup.object()
    //     .shape({ ...filterRuleSchema })
    //     .validate(
    //       { ...tempGlobalVariables, table_name: formData },
    //       { abortEarly: false }
    //     );
    // } catch (err: any) {
    //   err.inner.forEach((error: any) => {
    //     setErrors((prevError: any) => ({
    //       ...prevError,
    //       [error.path]: error.message,
    //     }));
    //   });
    //   return;
    // }

    if (mergeQueryType === "Query") {
      fetchExternalColumnsData?.setFormData((prevData: any) => {
        return {
          ...prevData,
          query: queryBuilderTempValue,
        };
      });

      setQueryBuilderTempValue(null);
      setTemporaryQueryValue(null);
    }

    if (!isFinalQuery) {
      setMixedFormData((prevData: any) =>
        prevData.map((item: any, i: any) =>
          i === index
            ? {
                ...item,
                data_pull_query: queryBuilderTempValue,
              }
            : item,
        ),
      );
      setQueryBuilderTempValue(null);
      setTemporaryQueryValue(null);
      setFormData((prev: any) => ({
        ...prev,
        table_name: "",
      }));
    }
    if (queryType === "mixedDependencyColumns") {
      setMixedFormData((prevData: any) => {
        return prevData.map((item: any, i: any) => {
          return i === index
            ? {
                ...item,
                external_source_name: formData?.external_source_name,
                create_temp_table_index: formData?.create_temp_table_index,
              }
            : item;
        });
      });
    }
    if (
      mergeQueryType === "External" &&
      typeof externalCommonValues?.setTempTableIndex === "function"
    ) {
      externalCommonValues.setTempTableIndex(formData?.create_temp_table_index);
    }
    if (
      mergeQueryType === "External" &&
      typeof externalCommonValues?.setTempPermanentTable === "function"
    ) {
      externalCommonValues.setTempPermanentTable(
        formData?.create_permanent_table,
      );
    }

    // if (queryType === "mixed") {
    //   setMixedFormData((prevData: any) =>
    //     prevData.map((item: any, i: any) =>
    //       i === index
    //         ? {
    //             ...item,
    //             fields: {
    //               ...item.fields,
    //               data_pull_query: query,
    //             },
    //           }
    //         : item
    //     )
    //   );
    // } else {
    //   setQueryBuilderTempValue(query);

    // }

    // Reset values
    //setQueryBuilderTempValue(`SELECT * FROM <> WHERE [] in`);
    setTemporaryQueryValue(query);
    setIsOpen(false);
    setOpenQueryDialogIndex(null);
    setErrors({});
    setColumns((prev: any) => ({
      ...prev,
      fetchedColumns: [],
    }));
    setFormData((prev: any) => ({
      ...prev,
      table_name: "",
    }));
  };

  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...formData, [name]: value };
      await externalGenericQueryFetchColumnsSchema.validateAt(
        name,
        partialFormData,
      );
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };

  const handleFetchColumns = async (index: any, itemId: number) => {
    try {
      if (mergeQueryType === "External") {
        let validateData = {};
        if (externalCommonValues?.fetchFrom === "BaseExternalBuilder") {
          validateData = {
            table_name: formData?.table_name,
            should_require_table_name: true,
            should_require_external_source_name: false,
          };
        } else {
          validateData = {
            table_name: formData?.table_name,
            external_source_name: formData?.external_source_name,
            should_require_table_name: true,
            should_require_external_source_name: true,
          };
        }
        await externalGenericQueryFetchColumnsSchema.validate(validateData, {
          abortEarly: false,
        });
      }

      let payload: any = {};
      if (externalCommonValues?.fetchFrom === "BaseExternalBuilder") {
        payload = {
          linked_service_id: String(
            externalCommonValues?.linkedServicesValue?.id,
          ),
          linked_service_code: externalCommonValues?.linkedServicesValue?.code,
          connection_key_id: externalCommonValues?.connectionKeysValue?.id,
          table_name: formData?.table_name,
        };
      } else if (externalCommonValues?.fetchFrom === "MixedExternalBuilder") {
        fetchExternalColumnsData?.mixedFormData.forEach((item: any) => {
          if (item.id === itemId) {
            payload = {
              linked_service_id: String(item?.linked_service_id),
              linked_service_code: item?.linked_service_code,
              connection_key_id: item?.connection_key_id,
              table_name: formData?.table_name,
            };
          }
        });
      }
      setFetchColumnsLoading(true);
      getTableColumns({
        payload: payload,
      })
        .then((response: any) => {
          if (response) {
            showToast(`Table columns fetched Successfully`, "success");
            const rawColumns = (Object.values(response)[0] as string[]) ?? [];
            const newFormattedColumns = rawColumns.map((col) => ({
              name: col,
              value: col,
            }));
            if (externalCommonValues?.fetchFrom === "MixedExternalBuilder") {
              setMixedFormData((prev: any) => {
                const updated = [...prev];
                updated[index].external_columns = newFormattedColumns;
                return updated;
              });
            }

            setColumns((prev: any) => ({
              ...prev,
              fetchedColumns: [
                {
                  name: formData?.table_name || "",
                  columns: newFormattedColumns,
                },
              ],
            }));

            setOpenQueryDialogIndex(index);
          } else {
            showToast(`Cannot fetched table columns`, "error");
          }
        })
        .catch((err: Error) => {
          console.error(err);
          showToast(`Cannot fetched table columns`, "error");
        })
        .finally(() => {
          setFetchColumnsLoading(false);
        });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      validationErrors.inner.forEach(
        (error: { path: string | number; message: string }) => {
          newErrors[error.path] = error.message;
        },
      );
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };
  const handleFetchFieldChange = async (e: any, type: string) => {
    const { name, value } = e.target;

    const shouldUpdateQuery = type === "table_name";

    const updatedQuery = shouldUpdateQuery
      ? updateQueryTableName(queryBuilderTempValue, value)
      : queryBuilderTempValue;

    // Update form data
    setFormData((prevFormData: any) => ({
      ...prevFormData,
      [name]: value,
    }));

    // Update query-related state - only update the working value, not the backup
    setQueryBuilderTempValue(updatedQuery);
    // DO NOT update temporaryQueryValue here - it should preserve the original value

    // Validate the field
    validateField(name, value);
  };
  const updateQueryTableName = (query: string, newTable: string) => {
    if (!query) return "";

    // Match "FROM" followed by <table> OR word characters (like crd)
    const regex = /(from\s+)(<[^<>]*>|\w*)/i;

    return query.replace(regex, `$1${newTable}`);
  };

  useEffect(() => {
    setFormData((prev: any) => ({
      ...prev,
      external_source_name:
        fetchExternalColumnsData?.mixedFormData[index]?.external_source_name,
      create_temp_table_index:
        fetchExternalColumnsData?.mixedFormData[index]
          ?.create_temp_table_index || externalCommonValues?.tempTableIndex,
      create_permanent_table:
        fetchExternalColumnsData?.mixedFormData[index]
          ?.create_permanent_table || externalCommonValues?.tempPermanentTable,
    }));
  }, [isOpen]);

  return (
    <>
      <Grid item xl={3} lg={3} md={3} sm>
        <Box
          sx={{
            display: { md: "block", sm: "none" },
          }}
          className="label-text line-height16"
        >
          <span className="position-relative">
            {title ? title : "SQL query"}
            {mergeQueryType && isFinalQuery && (
              <Tooltip
                title={
                  <Typography
                    sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap" }}
                  >
                    {[
                      "Configure the Final query here. Please use the unique identifiers for each Sub queries as",
                      ...(fetchExternalColumnsData?.mixedFormData || [])
                        .filter(Boolean)
                        .map((item: any, i: number) => {
                          const identifier =
                            item?.sub_type === "Resource"
                              ? item?.resource_code || ""
                              : item?.external_source_name || "";

                          // Use the user-defined friendly name as the display label.
                          // Falls back to "Sub Query N" when the name field is empty.
                          const displayLabel =
                            item?.sub_type === "External"
                              ? item?.external_source_name?.trim() ||
                                `Sub Query ${i + 1}`
                              : item?.sub_type === "Resource"
                                ? item?.resource_code?.trim() ||
                                  `Sub Query ${i + 1}`
                                : `Sub Query ${i + 1}`;

                          return `    -> ${displayLabel} : ${
                            identifier ? `<${identifier}>` : ""
                          }`;
                        }),
                    ].join("\n")}
                  </Typography>
                }
                placement="top"
                arrow
              >
                <span className="upload-icon-info">
                  <InfoIcon />
                </span>
              </Tooltip>
            )}
            {(mergeQueryType === "Resource" ||
              queryType === "resource" ||
              queryType === "Merged_Data" ||
              queryType === "Missing_Data" ||
              queryType === "Mismatched_Data" ||
              mergeQueryType === "External" ||
              mergeQueryType === "Merged_Data") && (
              <Tooltip
                componentsProps={{
                  tooltip: { className: "wide-tooltip w-380" },
                }}
                title={
                  <>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        whiteSpace: "pre-wrap",
                        mb: 1,
                      }}
                    >
                      {RULE_RESEARCH_QUERY_SYNTAX_HELP_TEXT}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        whiteSpace: "pre-wrap",
                        mb: 1,
                      }}
                    >
                      {["Examples:"].join("\n")}
                    </Typography>

                    <Typography
                      sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap" }}
                    >
                      {mergeQueryType === "Resource" || queryType === "resource"
                        ? RULE_RESOURCE_QUERY_TEMPLATES.join("\n")
                        : mergeQueryType === "External"
                          ? RULE_EXTERNAL_QUERY_TEMPLATES.join("\n")
                          : mergeQueryType === "Merged_Data" ||
                              queryType === "Merged_Data"
                            ? RULE_MERGED_QUERY_TEMPLATES.join("\n")
                            : queryType === "Missing_Data"
                              ? RULE_MISSING_DATA_QUERY_TEMPLATES.join("\n")
                              : queryType === "Mismatched_Data"
                                ? RULE_MISMATCHED_DATA_QUERY_TEMPLATES.join(
                                    "\n",
                                  )
                                : ""}
                    </Typography>
                  </>
                }
                placement="top"
                arrow
              >
                <span className="upload-icon-info">
                  <InfoIcon />
                </span>
              </Tooltip>
            )}
          </span>
        </Box>
        <TextField
          type="text"
          required
          // value={
          //   isFinalQuery
          //     ? fetchExternalColumnsData?.formData?.query || ""
          //     : fetchExternalColumnsData?.mixedFormData[index]?.fields
          //         ?.data_pull_query || ""
          // }
          value={
            mergeQueryType === "Query"
              ? fetchExternalColumnsData?.formData?.query
              : isFinalQuery
                ? queryBuilderTempValue || query
                : fetchExternalColumnsData?.mixedFormData[index]
                    ?.data_pull_query
          }
          onClick={() => {
            try {
              // Capture the original query value before opening the dialog
              const originalQuery =
                mergeQueryType === "Query"
                  ? fetchExternalColumnsData?.formData?.query
                  : isFinalQuery
                    ? queryBuilderTempValue || query
                    : fetchExternalColumnsData?.mixedFormData[index]
                        ?.data_pull_query;

              // Set the backup value immediately
              setTemporaryQueryValue(originalQuery || "");

              setIsOpen(true);
              setOpenQueryDialogIndex(index);
              if (queryType === "mixed") {
                setColumns((prev: any) => ({
                  ...prev,
                  resourceColumns: [],
                }));
              }
              const usedResourceIds =
                fetchExternalColumnsData?.mixedFormData.map((item: any) => {
                  return item.resource_id;
                });
              if (queryType === "mixedDependencyColumns") {
                const mixedFormData = fetchExternalColumnsData?.mixedFormData;
                const currentEntry = mixedFormData?.[index];
                const mixedDepColumns: any[] = [];

                const dependencies = currentEntry?.dependency_list;
                if (Array.isArray(dependencies) && dependencies.length > 0) {
                  setColumns((prev: any) => {
                    dependencies.forEach((dependencyMarker: string) => {
                      if (!dependencyMarker) return;

                      const matchedDependency = mixedFormData?.find(
                        (item: any) =>
                          item?.dependency_marker === dependencyMarker &&
                          item?.sub_type === "Resource" &&
                          !!item?.resource_id,
                      );

                      if (matchedDependency) {
                        const resourceId = matchedDependency.resource_id;
                        const resourceCode =
                          matchedDependency?.resource_code ||
                          `Resource_${resourceId}`;

                        const resourceColumns =
                          prev?.mixedQueryData?.[resourceId]?.columns;

                        if (Array.isArray(resourceColumns)) {
                          mixedDepColumns.push({
                            name: resourceCode,
                            columns: resourceColumns,
                          });
                        }
                      }
                    });

                    return {
                      ...prev,
                      mixedDependencyColumns: mixedDepColumns,
                      usedResourceIds: usedResourceIds,
                    };
                  });
                } else {
                  setColumns((prev: any) => ({
                    ...prev,
                    mixedDependencyColumns: [],
                    usedResourceIds: usedResourceIds,
                  }));
                }
              } else {
                setColumns((prev: any) => ({
                  ...prev,
                  mixedDependencyColumns: [],
                  usedResourceIds: usedResourceIds,
                }));
              }
            } catch (error) {
              console.error(
                "Error in processing mixed dependency columns:",
                error,
              );
            }
          }}
          className={`form-control-autocomplete ${
            errors?.queryBuilder ? "has-error" : ""
          }`}
          InputProps={{
            readOnly: true,
          }}
          error={!!errors?.queryBuilder}
          helperText={errors?.queryBuilder}
        />
      </Grid>

      <EnhancedQueryDialog
        fullWidth
        maxWidth="xl"
        open={isOpen}
        onClose={handleCloseQueryModal}
        className="main-dailog main-query-builder-dialog"
        hasUnsavedChanges={
          Boolean(query?.trim()) ||
          Object.keys(tempGlobalVariables).length > 0 ||
          Boolean(formData.external_source_name?.trim()) ||
          Boolean(formData.table_name?.trim())
        }
        confirmationTitle="Unsaved Query Changes"
        confirmationContent="You have unsaved query changes that will be lost. Are you sure you want to close?"
      >
        <Loader isLoading={fetchColumnsLoading} />
        <Box sx={{ padding: 3 }}>
          <Grid container className="dailog-header">
            <label className="label-text pt-13">
              Add Query
              <Tooltip
                componentsProps={{
                  tooltip: {
                    className: "sql-writing-guide",
                  },
                }}
                title={
                  <>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        whiteSpace: "pre-wrap",
                        mb: 1,
                      }}
                    >
                      {RULE_RESEARCH_QUERY_SYNTAX_HELP_TEXT}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        whiteSpace: "pre-wrap",
                        mb: 1,
                      }}
                    >
                      {["Examples:"].join("\n")}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap" }}
                    >
                      {mergeQueryType === "Resource" || queryType === "resource"
                        ? RULE_RESOURCE_QUERY_TEMPLATES.join("\n")
                        : mergeQueryType === "External"
                          ? RULE_EXTERNAL_QUERY_TEMPLATES.join("\n")
                          : mergeQueryType === "Merged_Data" ||
                              queryType === "Merged_Data"
                            ? RULE_MERGED_QUERY_TEMPLATES.join("\n")
                            : queryType === "Missing_Data"
                              ? RULE_MISSING_DATA_QUERY_TEMPLATES.join("\n")
                              : queryType === "Mismatched_Data"
                                ? RULE_MISMATCHED_DATA_QUERY_TEMPLATES.join(
                                    "\n",
                                  )
                                : [
                                    "SQL Writing Guide:",
                                    ...RULE_RESOURCE_QUERY_TEMPLATES,
                                    ...RULE_MERGED_QUERY_TEMPLATES,
                                    ...RULE_MISSING_DATA_QUERY_TEMPLATES,
                                    ...RULE_MISMATCHED_DATA_QUERY_TEMPLATES,
                                    ...RULE_EXTERNAL_QUERY_TEMPLATES,
                                  ].join("\n")}
                    </Typography>
                  </>
                }
                placement="top"
                arrow
              >
                <InfoIcon sx={{ cursor: "pointer", ml: 1 }} />
              </Tooltip>
            </label>
            <div className="close-icon" onClick={handleCloseQueryModal}></div>
          </Grid>
          <Box className="dailog-body pb-24">
            <Grid
              container
              columnSpacing={2.5}
              className="query-builder-layout"
            >
              <Grid item xs={12} md={8} className="query-editor-column">
                <QueryBuilder
                  handleChange={handleChangeQuery}
                  error={errors}
                  setErrors={setErrors}
                  mergeQuerycolumns={columns}
                  isFinalQuery={isFinalQuery}
                  dialogType={queryType ?? ""}
                  queryBuilderType={queryBuilderType}
                  mergeQueryType={mergeQueryType}
                />
              </Grid>
              <Grid item xs={12} md={4} className="query-sidebar-column">
                <Grid container rowSpacing={1.5}>
                  {externalCommonValues &&
                    mergeQueryType === "External" &&
                    Object.keys(externalCommonValues).length > 0 && (
                      <>
                        {externalCommonValues?.fetchFrom ===
                          "MixedExternalBuilder" && (
                          <Grid item xs={12}>
                            <TextField
                              name="external_source_name"
                              label={
                                <span>
                                  External Source Name
                                  <span className="required-asterisk">*</span>
                                </span>
                              }
                              type="text"
                              fullWidth
                              variant="outlined"
                              value={formData?.external_source_name}
                              onChange={(e) => {
                                const value = e.target.value;
                                // Validate input using utility function
                                if (
                                  !handleAlphanumericChange(
                                    value,
                                    showToast,
                                    "External Source Name",
                                  )
                                ) {
                                  return;
                                }
                                handleFetchFieldChange(
                                  e,
                                  "external_source_name",
                                );
                              }}
                              onKeyDown={(e) => {
                                handleAlphanumericKeyDown(
                                  e,
                                  showToast,
                                  "External Source Name",
                                );
                              }}
                              error={!!errors?.external_source_name}
                              helperText={
                                errors?.external_source_name ||
                                "(Only letters, numbers, and underscore are allowed)"
                              }
                              className={`form-control-autocomplete ${
                                errors?.external_source_name ? "has-error" : ""
                              }`}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={externalCommonValues?.linkedServices ?? []}
                            getOptionLabel={(option: any) => option.name || ""}
                            value={externalCommonValues?.linkedServicesValue}
                            disabled
                            renderInput={(params) => (
                              <TextField
                                label={
                                  <span>
                                    Connection Type
                                    <span className="required-asterisk">*</span>
                                  </span>
                                }
                                name="linked_service"
                                style={{ color: "#000000" }}
                                {...params}
                                placeholder="Select..."
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                error={
                                  !!externalCommonValues?.queryError
                                    ?.linked_service_id
                                }
                                helperText={
                                  externalCommonValues?.queryError
                                    ?.linked_service_id || ""
                                }
                              />
                            )}
                            renderOption={(params: any, item: any) => (
                              <li
                                {...params}
                                key={item.key}
                                style={{
                                  paddingTop: "2px",
                                  paddingBottom: "2px",
                                }}
                              >
                                <ListItemText>{item.name}</ListItemText>
                              </li>
                            )}
                            loadingText="Loading..."
                            onChange={(e, value) =>
                              externalCommonValues?.handleLinkedServiceChange(
                                index,
                                "linked_service_id",
                                value,
                              )
                            }
                            className={`form-control-autocomplete ${
                              externalCommonValues?.queryError
                                ?.linked_service_id
                                ? "has-error"
                                : ""
                            }`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Autocomplete
                            fullWidth
                            options={externalCommonValues?.connectionKeys}
                            getOptionLabel={(connectionData: any) =>
                              connectionData.name
                            }
                            disabled
                            value={externalCommonValues?.connectionKeysValue}
                            renderInput={(params) => (
                              <TextField
                                name="connection_key"
                                style={{ color: "#000000" }}
                                {...params}
                                label={
                                  <span>
                                    Connection Key
                                    <span className="required-asterisk">*</span>
                                  </span>
                                }
                                placeholder="Select..."
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                error={
                                  !!externalCommonValues?.queryError
                                    ?.connection_key_id
                                }
                                helperText={
                                  externalCommonValues?.queryError
                                    ?.connection_key_id
                                }
                              />
                            )}
                            loadingText="Loading..."
                            onChange={(e, value) =>
                              externalCommonValues?.handleFieldChange(
                                index,
                                "connection_key_id",
                                value?.id,
                              )
                            }
                            className={`form-control-autocomplete ${
                              externalCommonValues?.queryError
                                ?.connection_key_id
                                ? "has-error"
                                : ""
                            }`}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            name="table_name"
                            label={<span>Table Name</span>}
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={formData?.table_name}
                            onChange={(e) =>
                              handleFetchFieldChange(e, "table_name")
                            }
                            error={!!errors?.table_name}
                            helperText={errors?.table_name || ""}
                            className={`form-control-autocomplete ${
                              errors?.table_name ? "has-error" : ""
                            }`}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            color="secondary"
                            variant="contained"
                            onClick={() =>
                              handleFetchColumns(
                                index,
                                externalCommonValues?.itemId,
                              )
                            }
                            className="btn-orange btn-blue"
                          >
                            fetch columns
                          </Button>
                        </Grid>
                        {(queryBuilderType === "external_query" ||
                          queryBuilderType === "rule_research_query") && (
                          <>
                            <Grid item xs={12}>
                              <label className="base-resource-checkbox m-0">
                                <Checkbox
                                  checked={
                                    formData?.create_temp_table_index || false
                                  }
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    setFormData({
                                      ...formData,
                                      create_temp_table_index:
                                        event.target.checked,
                                    });
                                  }}
                                  sx={{
                                    "&.Mui-checked": {
                                      color: "#537ea0",
                                    },
                                  }}
                                />
                                <span>Create index on temporary tables</span>
                              </label>
                            </Grid>
                            <Grid item xs={12}>
                              <label className="base-resource-checkbox m-0">
                                <Checkbox
                                  checked={
                                    formData?.create_permanent_table || false
                                  }
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    setFormData({
                                      ...formData,
                                      create_permanent_table:
                                        event.target.checked,
                                    });
                                  }}
                                  sx={{
                                    "&.Mui-checked": {
                                      color: "#537ea0",
                                    },
                                  }}
                                />
                                <span>Create permanent table</span>
                              </label>
                            </Grid>
                          </>
                        )}
                      </>
                    )}

                  <QueryTemplatesCard
                    templates={
                      mergeQueryType === "Resource" || queryType === "resource"
                        ? RULE_RESOURCE_QUERY_TEMPLATES
                        : mergeQueryType === "External"
                          ? RULE_EXTERNAL_QUERY_TEMPLATES
                          : mergeQueryType === "Merged_Data" ||
                              queryType === "Merged_Data"
                            ? RULE_MERGED_QUERY_TEMPLATES
                            : queryType === "Missing_Data"
                              ? RULE_MISSING_DATA_QUERY_TEMPLATES
                              : queryType === "Mismatched_Data"
                                ? RULE_MISMATCHED_DATA_QUERY_TEMPLATES
                                : [
                                    ...RULE_RESOURCE_QUERY_TEMPLATES,
                                    ...RULE_EXTERNAL_QUERY_TEMPLATES,
                                    ...RULE_MERGED_QUERY_TEMPLATES,
                                    ...RULE_MISSING_DATA_QUERY_TEMPLATES,
                                    ...RULE_MISMATCHED_DATA_QUERY_TEMPLATES,
                                  ]
                    }
                    title="Query Templates"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <DialogActions className="dailog-footer">
            <Button
              color="secondary"
              variant="contained"
              onClick={handleSaveQuery}
              className="btn-orange"
            >
              <SaveOutlinedIcon /> &nbsp; Save
            </Button>
          </DialogActions>
        </Box>
      </EnhancedQueryDialog>
    </>
  );
};

export default QueryBuilderDialog;
